import { useCallback, useRef } from "react";
import { AgGridReact } from "ag-grid-react";
import type { GridReadyEvent, RowClickedEvent } from "ag-grid-community";
import { globalDefaultColDef } from "@/setup/ag-grid/mesf-theme";
import type { PassType } from "../models/PassType";
import { useTableData } from "../hooks/useTableData";

interface PassTypesGridProps {
	passTypes: PassType[];
	selectedPassType: PassType | null;
	onSelectPassType: (passType: PassType | null) => void;
	onDelete: (passType: PassType) => void;
}

export const PassTypesGrid = ({
	passTypes,
	selectedPassType,
	onSelectPassType,
	onDelete,
}: PassTypesGridProps) => {
	const gridRef = useRef<AgGridReact<PassType>>(null);
	const { columnDefs } = useTableData({ onDelete });

	const onGridReady = useCallback((_params: GridReadyEvent<PassType>) => {
		// Let flex columns handle sizing naturally
	}, []);

	const handleRowClicked = useCallback(
		(event: RowClickedEvent<PassType>) => {
			if (event.data) {
				onSelectPassType(event.data);
			}
		},
		[onSelectPassType],
	);

	const getRowId = useCallback((params: { data: PassType }) => {
		return params.data.Id?.toString() ?? "";
	}, []);

	return (
		<AgGridReact<PassType>
			ref={gridRef}
			rowData={passTypes}
			columnDefs={columnDefs}
			defaultColDef={globalDefaultColDef}
			onGridReady={onGridReady}
			onRowClicked={handleRowClicked}
			getRowId={getRowId}
			rowSelection="single"
			suppressCellFocus
			suppressContextMenu
			alwaysShowVerticalScroll
			alwaysShowHorizontalScroll
			suppressColumnVirtualisation
			rowHeight={36}
			headerHeight={36}
			getRowStyle={(params) => {
				if (selectedPassType?.Id === params.data?.Id) {
					return { backgroundColor: "#e3f2fd", color: "inherit" };
				}
				if (params.data && !params.data.Active) {
					return { backgroundColor: "#f5f5f5", color: "#9e9e9e" };
				}
				return undefined;
			}}
		/>
	);
};
